import {
  CONTENT_TYPE,
  DEFAULT_FILL_COLOR,
  DEFAULT_LINE_SPACING,
} from '../constants';
import { MAX_CREATIVE_DIMENSION_LENGTH_IN_PX } from '../features/creative/constants';
import {
  OVERFLOW_X,
  OVERFLOW_Y,
  VERTICAL_TEXT_ALIGNMENT,
} from '../features/text/constants';
import { isGroup } from '../features/group/util';
import * as MediaSelectors from './media';
import { warn } from '../util/log';
import {
  getIsPublisherDynamicEditions,
  getIsPublisherHappeningNow
} from '../features/discover/state-management/selectors';
import { getTotalTime } from '../features/shared/playback/state-management/selectors';
import {
  isEmpty as isEmptyArray,
} from '../util/array';
import {
  isBoolean,
  isEmpty as isEmptyObject,
  isNil,
  isNotNil,
} from '../util/object';
import { isNumber } from '../util/numbers';
import { isOverflowYHidden } from '../features/text/util';
import { createSelector } from 'reselect';
import { convertPixelToEm } from '../util/font';
import {
  getElementByContent,
} from './element';
import { getVideoRelativeFrameIndicesAndTimesForFrame } from '../util/videoExtraction/frameUtils';
import { createKeyedStateSelector } from '../features/shared/utils/selectorUtils';
import { getTextAlign } from '../util/domUtils';
import { getCurrentFrame } from '../features/playback/manager/PlaybackManager';

export const getAllIds = (state) => state.content.content;
export const getAllContentById = (state) => state.content.contentById;

// @todo(hamik): move into another file? https://jira.sc-corp.net/browse/ADS-37067
export const getByIdHelper = (allContentById, contentId) => {
  const content = allContentById[contentId];
  if (!content) {
    warn(`No content found with id ${contentId}`);
    return null;
  }
  return content;
};

export const getById = createKeyedStateSelector(
  [getAllContentById, (state, contentId) => contentId],
  (allContentById, contentId) => getByIdHelper(allContentById, contentId),
);

export const getTypeById = createKeyedStateSelector([getById], (content) => {
  if (!content) {
    return null;
  }

  return content.contentType;
});

export const getAllContent = (state, contentType = null) => {
  return Object.values(state.content.contentById).filter(
    (content) => !contentType || contentType === content.contentType,
  );
};

export const getContentByMediaId = (state, mediaId, contentType) => {
  return getAllContent(state, contentType).filter(
    (content) => content && content.mediaId === mediaId,
  );
};

export const getAllText = (state) => getAllContent(state, CONTENT_TYPE.TEXT);
export const getAllImages = (state) => getAllContent(state, CONTENT_TYPE.IMAGE);
export const getAllSVG = (state) => getAllContent(state, CONTENT_TYPE.SVG);
export const getAllShapes = (state) => getAllContent(state, CONTENT_TYPE.SHAPE);
export const getAllVideos = (state) => getAllContent(state, CONTENT_TYPE.VIDEO);
export const getAllAudio = (state) => getAllContent(state, CONTENT_TYPE.AUDIO);
export const getAllElementGroups = (state) =>
  getAllContent(state, CONTENT_TYPE.ELEMENT_GROUP);
export const getAllEffectElementGroups = (state) =>
  getAllContent(state, CONTENT_TYPE.ELEMENT_EFFECT_GROUP);
export const getAllGroups = createSelector(
  [getAllElementGroups, getAllEffectElementGroups],
  (elementGroups, effectElementGroups) => {
    return [...elementGroups, ...effectElementGroups];
  },
);

// @todo(hamik): move into another file? https://jira.sc-corp.net/browse/ADS-37067
export const getMediaHelper = (contentById, contentId, mediaById) => {
  const content = getByIdHelper(contentById, contentId);
  if (!content) {
    return null;
  }
  const { mediaId } = content;
  if (isNil(mediaId)) {
    return null;
  }
  return MediaSelectors.getByIdHelper(mediaById, mediaId);
};

// @todo(hamik): maybe turn into a keyed selector. waiting for https://jira.sc-corp.net/browse/ADS-37067
export const getMedia = createSelector(
  [
    getAllContentById,
    MediaSelectors.getAllMediaById,
    (state, contentId) => contentId,
  ],
  (contentById, mediaById, contentId) =>
    getMediaHelper(contentById, contentId, mediaById),
);

export const getMediaId = createKeyedStateSelector([getById], (content) => {
  if (!content) {
    return null;
  }

  return content.mediaId;
});

export const getFillColor = createKeyedStateSelector([getById], (content) => {
  if (!content) {
    return null;
  }

  return content.fillColor;
});

export const getTextString = createKeyedStateSelector([getById], (content) => {
  if (!content) {
    return null;
  }

  return content.textString;
});

export const getTextEffect = createKeyedStateSelector([getById], (content) => {
  if (!content) {
    return null;
  }

  return content.textEffect;
});

export const getTextProperties = createKeyedStateSelector(
  [getById],
  (content) => {
    if (!content) {
      return null;
    }

    if (content.contentType !== CONTENT_TYPE.TEXT) return null;

    const {
      alignment,
      fontSize,
      font,
      textString,
      fillColor = DEFAULT_FILL_COLOR,
      letterSpacing = 0,
      lineSpacing = DEFAULT_LINE_SPACING,
      overflowX,
      overflowY = OVERFLOW_Y.HIDDEN,
      padding = 0,
      textFill: textFillProperties,
      verticalAlignment: contentVerticalAlignment,
    } = content;

    const textAlign = getTextAlign(alignment);
    const verticalAlignment =
      overflowX !== OVERFLOW_X.WRAP /* is not wrapping */
        ? VERTICAL_TEXT_ALIGNMENT.TOP /* default to top */
        : contentVerticalAlignment;

    return {
      alignment,
      fillColor,
      font,
      fontSize,
      letterSpacing,
      lineSpacing,
      overflowX,
      overflowY,
      padding,
      textAlign,
      textFillProperties,
      textString,
      verticalAlignment,
    };
  },
);

export const getTextStyle = createKeyedStateSelector(
  [getTextProperties],
  (textProperties) => {
    if (!textProperties) {
      return null;
    }

    const {
      fillColor,
      font,
      fontSize,
      letterSpacing,
      lineSpacing,
      padding,
      textAlign,
      verticalAlignment: verticalAlign,
    } = textProperties;

    return {
      fontSize,
      padding,
      textAlign,
      verticalAlign,

      color: fillColor,
      fontFamily: font,
      letterSpacing: `${convertPixelToEm(letterSpacing)}em`,
      lineHeight: `${lineSpacing}em`,
    };
  },
);

export const getSVGStyle = createKeyedStateSelector(
  [getTextStyle],
  (textStyle) => {
    if (!textStyle) {
      return null;
    }

    const svgStyle = Object.assign({}, textStyle);

    // alignment must be handled at the line level for SVG
    delete svgStyle.textAlign;

    // color in CSS is fill in SVG
    const fill = svgStyle.color;
    delete svgStyle.color;
    svgStyle.fill = fill;

    return svgStyle;
  },
);

export const getHeight = createKeyedStateSelector(
  [getMedia, getTypeById],
  (media, contentType) => {
    // Media is nil on content type = ELEMENT_GROUP, ELEMENT_EFFECT_GROUP
    // TODO use Rectangle to get actual width height of group.
    if (isGroup(contentType)) return MAX_CREATIVE_DIMENSION_LENGTH_IN_PX;
    if (isNil(media)) return null;

    return media.height;
  },
);

export const getWidth = createKeyedStateSelector(
  [getMedia, getTypeById],
  (media, contentType) => {
    // Media is nil on content type = ELEMENT_GROUP, ELEMENT_EFFECT_GROUP
    // TODO use Rectangle to get actual width height of group.
    if (isGroup(contentType)) return MAX_CREATIVE_DIMENSION_LENGTH_IN_PX;
    if (isNil(media)) return null;
    return media.width;
  },
);

export const getTextWrapProperties = createKeyedStateSelector(
  [getById],
  (content) => {
    return !isEmptyObject(content) ? content.textWrap : null;
  },
);

export const getTextWrapHeight = createKeyedStateSelector(
  [getTextWrapProperties, getHeight],
  (textWrapProperties, height) => {
    return !isEmptyObject(textWrapProperties) &&
      isNumber(textWrapProperties.height)
      ? textWrapProperties.height
      : height;
  },
);

export const getTextWrapWidth = createKeyedStateSelector(
  [getTextWrapProperties, getWidth],
  (textWrapProperties, width) => {
    return !isEmptyObject(textWrapProperties) &&
      isNumber(textWrapProperties.width)
      ? textWrapProperties.width
      : width;
  },
);

export const getTextWrapBreakWord = createKeyedStateSelector(
  [getTextWrapProperties],
  (textWrapProperties) => {
    return !isEmptyObject(textWrapProperties) &&
      isBoolean(textWrapProperties.breakWord)
      ? textWrapProperties.breakWord
      : false;
  },
);

export const isTextWrap = createKeyedStateSelector([getById], (content) => {
  return !isEmptyObject(content)
    ? content.overflowX === OVERFLOW_X.WRAP
    : false;
});

export const getNaturalHeight = createKeyedStateSelector(
  [getById],
  (content) => {
    return !isEmptyObject(content) ? content.naturalHeight : 0;
  },
);

export const getNaturalWidth = createKeyedStateSelector(
  [getById],
  (content) => {
    return !isEmptyObject(content) ? content.naturalWidth : 0;
  },
);


export const getOverflowY = createKeyedStateSelector([getById], (content) => {
  return !isEmptyObject(content) ? content.overflowY : undefined;
});

export const isTextOverflowHidden = createKeyedStateSelector(
  [isTextWrap, getOverflowY],
  (textWrapped, overflowY) => {
    return textWrapped && isOverflowYHidden(overflowY);
  },
);

export const shouldDisallowTextOverflow = createKeyedStateSelector(
  [getById, getIsPublisherHappeningNow, getIsPublisherDynamicEditions],
  (content, isPublisherHappeningNow, isPublisherDynamicEditions) => {
    if (isNil(content)) return false;

    // All text layers for HN are not allowed to overflow
    const { contentType } = content;
    return (contentType === CONTENT_TYPE.TEXT) && (isPublisherHappeningNow || isPublisherDynamicEditions);
});

/*
  NOTE: fontAdjustment was a value saved on text so that different font features like ascenders and descenders
  could fit within the context of a clipped canvas SVG. Now that we've solved the problem of SVG clipping,
  this feature is no longer needed. However, we do need to support it on the off change an old creative
  still has it set.
*/
export const getFontAdjustment = createKeyedStateSelector(
  [getMedia],
  (media) => {
    if (!media) return 0;
    return isNil(media.fontAdjustment) ? 0 : media.fontAdjustment;
  },
);

export const getPadding = createKeyedStateSelector([getById], (content) => {
  if (!content) {
    return 0;
  }

  const { contentType, padding = 0 } = content;
  if (contentType !== CONTENT_TYPE.TEXT) return 0;
  return padding;
});

export const getTotalPadding = createKeyedStateSelector(
  [getFontAdjustment, getPadding],
  (fontAdjustment, padding) => {
    return 2 * (fontAdjustment + padding);
  },
);

export const getFitTextBox = createKeyedStateSelector(
  [getTotalPadding, getNaturalHeight],
  (totalPadding, naturalHeight) => {
    return totalPadding + naturalHeight;
  },
);

export const getStart = createKeyedStateSelector([getById], (content) => {
  if (!content) {
    return 0;
  }

  return content.start;
});

export const getEnd = createKeyedStateSelector([getById], (content) => {
  if (!content) {
    return 0;
  }

  return content.end;
});

const getMediaDuration = createKeyedStateSelector([getMedia], (media) => {
  if (!media) {
    return 0;
  }

  return media.duration;
});

const elementStartFramePassthrough = (state, contentId, startFrame) =>
  startFrame;
export const getVideoRelativeFrameIndicesAndTimes = createSelector(
  [
    getStart,
    getEnd,
    getMediaDuration,
    elementStartFramePassthrough,
    getCurrentFrame,
    getTotalTime,
  ],
  getVideoRelativeFrameIndicesAndTimesForFrame,
);

const startIndexPassthrough = (
  state,
  contentId,
  givenStartIndex,
  givenEndIndex,
) => givenStartIndex;
const endIndexPassthrough = (
  state,
  contentId,
  givenStartIndex,
  givenEndIndex,
) => givenEndIndex;
const getContentMediaUrlAndIndicesFromContentId = (state, contentId) => {
  const { startIndex, endIndex } = getVideoRelativeFrameIndicesAndTimes(
    state,
    contentId,
  );

  const content = getById(state, contentId);
  const element = getElementByContent(state, content);
  const media = getMedia(state, contentId);

  return {
    content,
    element,
    endIndex,
    media,
    startIndex,
  };
};

export const getExtractionArgumentsFromIndices = createSelector(
  [
    getContentMediaUrlAndIndicesFromContentId,
    startIndexPassthrough,
    endIndexPassthrough,
  ],
  (
    { content, element, endIndex, media, startIndex },
    forcedStartIndex,
    forcedEndIndex,
    url,
  ) => {
    const {
      frameStart: elementStartFrame,
      frameEnd: elementEndFrame,
    } = element;

    return {
      elementStartFrame,
      elementEndFrame,
      url,
      contentId: content.contentId,
      endIndex: isNotNil(forcedEndIndex) ? forcedEndIndex : endIndex,
      fileName: media.fileName,
      mediaId: media.mediaId,
      startIndex: isNotNil(forcedStartIndex) ? forcedStartIndex : startIndex,
    };
  },
);

export const getShape = createSelector([getById], (content) => {
  if (!content) {
    return null;
  }

  return content.shape;
});

export const getCreativeFieldId = createKeyedStateSelector(
  [getById],
  (content) => {
    if (!content) {
      return null;
    }
    return content.creativeFieldId;
  },
);

const isContentOverflowingHelper = (content, allMediaById) => {
  if (isNil(content)) return false;
  if (isNil(allMediaById)) return false;
  if (!content.visible) return false;
  const medium = allMediaById[content.mediaId];
  if (isNil(medium)) return false;
  const height = medium.height;
  const naturalTextHeight = content.naturalHeight;
  return (isNumber(height) && isNumber(naturalTextHeight)) && naturalTextHeight > height;
};

export const isContentOverflowing = createKeyedStateSelector(
  [getById, MediaSelectors.getAllMediaById],
  (content, allMediaById) => {
    return isContentOverflowingHelper(content, allMediaById);
  }
);

export const getHasTextLayersWithDisallowedOverflow = createKeyedStateSelector(
  [getIsPublisherHappeningNow, getIsPublisherDynamicEditions, getAllText, MediaSelectors.getAllMediaById],
  (isPublisherHappeningNow, isPublisherDynamicEditions, allTextLayers, allMediaById) => {
    if (!(isPublisherHappeningNow || isPublisherDynamicEditions)) return false;
    if (isEmptyArray(allTextLayers)) return false;
    if (isNil(allMediaById)) return false;
    return allTextLayers.some(content => {
      return isContentOverflowingHelper(content, allMediaById);
    });
  }
);
